import type { ContractorCampaignModel } from 'entities/campaign';
import { ContractorCampaignsPageFilter, useGetCampaigns } from 'entities/campaign';
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { pageRoutes } from 'shared/routes';
import { Loader } from 'shared/ui/Loader';
import { NavigationTabs } from 'shared/ui/NavigationTabs';
import { initTS, InfoBoxTSReport } from 'widgets/ContractorTSReports';

import { CAMPAIGN_CREATION_QUERY_KEY } from './ContractorCampaigns';

interface ContractorReportsProps {
  children: JSX.Element;
}

export function ContractorReports({ children }: ContractorReportsProps) {
  const history = useHistory();
  const location = useLocation();
  const getCampaignsQuery = useGetCampaigns<ContractorCampaignModel>({
    urlFilters: `?${ContractorCampaignsPageFilter.statusArray}=running`,
  });

  const isLoading = !getCampaignsQuery.data;
  const isNoCampaigns = getCampaignsQuery.data?.meta.total_without_filters === 0;
  const isNoRunningCampaigns = getCampaignsQuery.data?.meta.total === 0;

  const getTabId = () => {
    switch (location.pathname) {
      case pageRoutes.app.reportsPerformance:
        return 1;
      case pageRoutes.app.reportsCampaignMap:
        return 2;
      case pageRoutes.app.reportsVerticalBenchmark:
        return 3;
      default:
        return 0;
    }
  };

  const tabs = [
    {
      id: 0,
      text: 'Overview',
      onClick: () => {
        history.push(pageRoutes.app.reportsOverview);
      },
    },
    {
      id: 1,
      text: 'Performance metrics',
      onClick: () => {
        history.push(pageRoutes.app.reportsPerformance);
      },
      tabWidth: 171,
    },
    {
      id: 2,
      text: 'Campaign expansion opportunities',
      onClick: () => {
        history.push(pageRoutes.app.reportsCampaignMap);
      },
      tabWidth: 262,
    },
    {
      id: 3,
      text: 'Vertical benchmark',
      onClick: () => {
        history.push(pageRoutes.app.reportsVerticalBenchmark);
      },
      tabWidth: 160,
    },
  ];

  useEffect(() => {
    if (!isNoCampaigns && !isNoRunningCampaigns) {
      initTS();
    }
  }, [isNoCampaigns, isNoRunningCampaigns]);

  if (isLoading) {
    return <Loader className="mt-20 flex justify-center" />;
  }

  if (isNoCampaigns) {
    return (
      <InfoBoxTSReport
        title="Start tracking your success!"
        text="Launch your first campaign to start seeing how you’re performing."
        buttonText="Create campaign"
        buttonOnClick={() => history.push(`${pageRoutes.app.campaigns}?${CAMPAIGN_CREATION_QUERY_KEY}=true`)}
      />
    );
  }

  if (isNoRunningCampaigns) {
    return (
      <InfoBoxTSReport
        title="Your reports will be here soon!"
        text="Once your campaign is running, you’ll start seeing insights about your performance."
      />
    );
  }

  return (
    <div className="min-w-[500px]">
      <NavigationTabs
        tabs={tabs}
        itemWidth={116}
        selectedId={getTabId()}
      />
      <div className="mt-6 rounded-[10px] bg-deep-blue-25 pb-2">{children}</div>
    </div>
  );
}
